// OWNER: Agent C. Country → account → holding tree. UK (and any non-INR)
// rows show both currencies; gain uses the arrow-glyph badge, never colour alone.
import { useNavigate } from "react-router";
import { Text } from "@cloudflare/kumo/components/text";
import { Empty } from "@cloudflare/kumo/components/empty";
import { Button } from "@cloudflare/kumo/components/button";
import { Table } from "@cloudflare/kumo/components/table";
import { LayerCard } from "@cloudflare/kumo/components/layer-card";
import { ChartLineUpIcon } from "@phosphor-icons/react";
import { formatUnits, formatMoney } from "@shared/format";
import { usePortfolioSummary, useIsAdmin } from "../../lib/hooks";
import { PageHeader } from "../../components/PageHeader";
import { MoneyDual } from "../../components/MoneyDual";
import { GainBadge } from "../../components/GainBadge";
import { StalePriceBanner } from "../../components/StalePriceBanner";
import { TableSkeleton } from "../../components/Skeletons";
import type { AccountValuation, Currency } from "@shared/types";

export default function InvestmentsPage() {
  const summary = usePortfolioSummary();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();

  if (summary.status === "loading") {
    return (
      <div>
        <PageHeader title="Investments" />
        <TableSkeleton rows={8} />
      </div>
    );
  }

  if (summary.status === "error") {
    return (
      <Empty
        title="Couldn't load your investments"
        description={summary.error.message}
        contents={
          <Button variant="primary" onClick={summary.reload}>
            Retry
          </Button>
        }
      />
    );
  }

  const s = summary.data;
  const fx = s.fx;
  const hasAny = s.countries.some((c) => c.accounts.some((a) => a.holdings.length > 0));

  return (
    <div className="space-y-8 pb-8">
      <PageHeader
        title="Investments"
        action={
          <Button
            variant="primary"
            disabled={!isAdmin}
            title={isAdmin ? undefined : "Admins only"}
            onClick={() => navigate("/transactions/new")}
          >
            Add transaction
          </Button>
        }
      />
      <StalePriceBanner warnings={s.warnings} />

      {!hasAny ? (
        <Empty
          icon={<ChartLineUpIcon size={48} className="text-kumo-inactive" />}
          title="No holdings yet"
          description="Once you record a transaction, holdings will appear here grouped by account."
          contents={
            <Button
              variant="primary"
              disabled={!isAdmin}
              title={isAdmin ? undefined : "Admins only"}
              onClick={() => navigate("/transactions/new")}
            >
              Add a transaction
            </Button>
          }
        />
      ) : (
        s.countries.map(
          (country) =>
            country.accounts.length > 0 && (
              <div key={country.country} className="space-y-4">
                <Text variant="heading" as="h2">
                  {country.country === "IN" ? "India" : "United Kingdom"}
                </Text>
                {country.accounts.map((account) => (
                  <AccountSection
                    key={account.id}
                    account={account}
                    fxRate={account.currency === "GBP" ? fx.GBPINR : account.currency === "USD" ? fx.USDINR : 1}
                    fxAsOf={fx.asOf}
                    onOpenHolding={(id) => navigate(`/investments/${id}`)}
                  />
                ))}
              </div>
            ),
        )
      )}
    </div>
  );
}

function AccountSection({
  account,
  fxRate,
  fxAsOf,
  onOpenHolding,
}: {
  account: AccountValuation;
  fxRate: number;
  fxAsOf: string;
  onOpenHolding: (id: number) => void;
}) {
  if (account.holdings.length === 0) return null;
  return (
    <LayerCard className="p-0">
      <LayerCard.Secondary className="flex items-center justify-between gap-4">
        <span>{account.name}</span>
        <div className="text-right text-kumo-subtle">
          <MoneyDual
            valueNative={account.valueNative}
            nativeCurrency={account.currency}
            valueInr={account.valueInr}
            fxRate={account.currency !== "INR" ? fxRate : undefined}
            fxAsOf={fxAsOf}
            size="sm"
          />
        </div>
      </LayerCard.Secondary>
      <LayerCard.Primary className="overflow-x-auto p-0">
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.Head>Holding</Table.Head>
              <Table.Head>Units</Table.Head>
              <Table.Head>Price</Table.Head>
              <Table.Head>Value</Table.Head>
              <Table.Head>Invested</Table.Head>
              <Table.Head>Gain</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {account.holdings.map((h) => (
              <Table.Row
                key={h.id}
                className="cursor-pointer"
                onClick={() => onOpenHolding(h.id)}
              >
                <Table.Cell>
                  <div className="flex items-center gap-2">
                    <span>{h.name}</span>
                    {h.isStale && (
                      <span className="rounded bg-kumo-warning-tint px-1.5 py-0.5 text-xs text-kumo-warning">stale</span>
                    )}
                  </div>
                </Table.Cell>
                <Table.Cell>{h.price != null ? formatUnits(h.units) : "—"}</Table.Cell>
                <Table.Cell>{h.price != null ? formatMoney(h.price, (h.priceCurrency ?? account.currency) as Currency) : "—"}</Table.Cell>
                <Table.Cell>
                  <MoneyDual
                    valueNative={h.valueNative}
                    nativeCurrency={account.currency}
                    valueInr={h.valueInr}
                    fxRate={account.currency !== "INR" ? fxRate : undefined}
                    fxAsOf={fxAsOf}
                    size="sm"
                  />
                </Table.Cell>
                <Table.Cell>{formatMoney(h.investedNative, account.currency)}</Table.Cell>
                <Table.Cell>
                  <GainBadge pct={h.gainPct} />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </LayerCard.Primary>
    </LayerCard>
  );
}
